/**
 * DocumentPreviewPanel
 *
 * Right panel component for previewing a document when viewing a match.
 * Provides a "back to call prep" button and delegates to BlobPreview for rendering.
 */
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';

import { BlobPreview } from '@/components/documents-v2/components/inspector/BlobPreview';
import { Button } from '@/components/ui/button';
import { useGetArtifactUnpackedQuery } from '@/services/api/artifactService';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { ArtifactType } from '@/services/types/artifact-digest-types';

interface DocumentPreviewPanelProps {
  artifactId: string | undefined;
  matterId: string;
  onBack: () => void;
}

export const DocumentPreviewPanel = ({
  artifactId,
  matterId,
  onBack,
}: DocumentPreviewPanelProps) => {

  const {
    data: artifact,
    isLoading,
    isError,
  } = useGetArtifactUnpackedQuery(
    { matterId: matterId || '', artifactId: artifactId || '' },
    { skip: !matterId || !artifactId },
  );

  // Get matter details for pinning functionality
  const { data: matter } = useGetMatterDetailsQuery(matterId || '', {
    skip: !matterId,
  });

  // Check if artifact is a blob type
  const isBlob = artifact?.artifact_type === ArtifactType.BLOB && artifact.blob;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-zinc-600 dark:text-zinc-400 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Call Prep</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-zinc-50 dark:bg-zinc-950 p-4">
        {/* No document selected */}
        {!artifactId && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <FileText className="h-12 w-12 text-zinc-300 dark:text-zinc-600" />
            <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
              No document selected
            </p>
          </div>
        )}

        {/* Loading state */}
        {artifactId && isLoading && (
          <div className="flex h-full flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400 dark:text-zinc-500" />
            <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
              Loading document...
            </p>
          </div>
        )}

        {/* Error state */}
        {artifactId && !isLoading && (isError || !artifact) && (
          <div className="flex h-full flex-col items-center justify-center text-center px-4">
            <FileText className="h-12 w-12 text-red-300 dark:text-red-400" />
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              Failed to load document
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="mt-4"
            >
              Go Back
            </Button>
          </div>
        )}

        {/* Blob preview - delegate to existing BlobPreview component */}
        {artifactId && !isLoading && artifact && isBlob && (
          <BlobPreview
            file={artifact.blob}
            matterId={matterId}
            matterName={matter?.name}
          />
        )}

        {/* Non-blob artifact types - show message */}
        {artifactId && !isLoading && artifact && !isBlob && (
          <div className="flex h-full flex-col items-center justify-center text-center px-4">
            <FileText className="h-12 w-12 text-zinc-400 dark:text-zinc-500" />
            <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
              Preview not available for this artifact type
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="mt-4"
            >
              Go Back
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
